import { useContext, useEffect, useState } from 'react';
import { ScrollRevealContext } from '../ScrollReveal';

/** Simule une saisie champ par champ et la rejoue depuis le début. */
const useProgressiveEntry = (text, { delay = 0, interval = 75, cycle = 8000, targetRef = null } = {}) => {
  const [value, setValue] = useState('');
  const isInView = useContext(ScrollRevealContext);
  const [targetInView, setTargetInView] = useState(!targetRef);

  useEffect(() => {
    if (!targetRef) {
      setTargetInView(true);
      return undefined;
    }

    const target = targetRef.current;
    if (!target || typeof IntersectionObserver === 'undefined') {
      setTargetInView(true);
      return undefined;
    }

    const observer = new IntersectionObserver(([entry]) => {
      setTargetInView(entry.isIntersecting && entry.intersectionRatio > 0);
    }, { threshold: 0, rootMargin: '0px' });
    observer.observe(target);
    return () => observer.disconnect();
  }, [targetRef]);

  useEffect(() => {
    if (!isInView || !targetInView) {
      setValue('');
      return undefined;
    }

    let cancelled = false;
    let timerId;

    const startCycle = () => {
      if (cancelled) return;
      setValue('');
      let index = 0;

      const typeNextCharacter = () => {
        if (cancelled) return;
        index += 1;
        setValue(text.slice(0, index));
        if (index < text.length) {
          timerId = setTimeout(typeNextCharacter, interval);
        } else {
          timerId = setTimeout(startCycle, Math.max(cycle - text.length * interval, 1200));
        }
      };

      if (text.length) timerId = setTimeout(typeNextCharacter, interval);
    };

    timerId = setTimeout(startCycle, delay);
    return () => {
      cancelled = true;
      clearTimeout(timerId);
    };
  }, [text, delay, interval, cycle, isInView, targetInView]);

  return value;
};

export default useProgressiveEntry;
